// © 2026 Camp Promethean, LLC. All Rights Reserved.
// Phase 13 · RB-4 — fetch the caller's effective access for the active facility.
// Cache successful responses per user/facility. Never expose the previous
// facility’s access during a switch; route guards show a retry state on failure.

import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useFacility } from "@/contexts/facility/FacilityContext";
import { supabase } from "@/integrations/supabase/client";
import type { EffectiveAccess } from "@/lib/effectiveAccess";

const cache = new Map<string, EffectiveAccess>();
const listeners = new Set<() => void>();

/** Drop the cache (call after seat/role/policy mutations so the UI re-resolves). */
export function invalidateEffectiveAccess() {
  cache.clear();
  listeners.forEach((notify) => notify());
}

export function useEffectiveAccess(): {
  access: EffectiveAccess | null;
  loading: boolean;
  error: boolean;
} {
  const { profile } = useAuth();
  const { activeFacility } = useFacility();
  const [resolved, setResolved] = useState<{ key: string; access: EffectiveAccess | null; error?: boolean } | null>(null);
  const requestKey = `${profile?.id ?? ""}:${activeFacility?.id ?? ""}`;
  const access = resolved?.key === requestKey ? resolved.access : null;
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const bump = () => setVersion((v) => v + 1);
    listeners.add(bump);
    return () => {
      listeners.delete(bump);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      if (!profile || !activeFacility) {
        setResolved({ key: requestKey, access: null });
        setLoading(false);
        return;
      }
      const key = `${profile.id}:${activeFacility.id}`;
      const cached = cache.get(key);
      if (cached) {
        setResolved({ key: requestKey, access: cached });
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const { data, error } = await (supabase as any).rpc("get_effective_access", {
          p_facility_id: activeFacility.id,
        });
        if (cancelled) return;
        if (error) throw error;
        const result = data as EffectiveAccess;
        cache.set(key, result);
        setResolved({ key: requestKey, access: result });
      } catch (err) {
        if (cancelled) return;
        console.error("get_effective_access failed:", err);
        setResolved({ key: requestKey, access: null, error: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    resolve();
    return () => {
      cancelled = true;
    };
  }, [profile, activeFacility, version, requestKey]);

  return { access, error: resolved?.key === requestKey && Boolean(resolved.error), loading: loading || (Boolean(profile && activeFacility) && resolved?.key !== requestKey) };
}

export default useEffectiveAccess;
